import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Row, Col, Card, Tag, Button, Modal, Form, Input, Select, Switch,
  message, Empty, Spin, Badge, Tooltip, Popconfirm, Typography, Tabs,
} from "antd";
import {
  PlusOutlined, PushpinOutlined, DeleteOutlined, EyeOutlined,
  GlobalOutlined, TeamOutlined, BankOutlined, SoundOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { SERVER_URL } from "../../../databucket";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const { TextArea } = Input;
const { Text, Title } = Typography;

const CATEGORY_CFG = {
  general: { color: "#6366F1", label: "General", icon: <SoundOutlined /> },
  policy:  { color: "#3B82F6", label: "Policy", icon: <BankOutlined /> },
  event:   { color: "#10B981", label: "Event", icon: <EyeOutlined /> },
  urgent:  { color: "#EF4444", label: "Urgent", icon: <SoundOutlined /> },
  holiday: { color: "#F59E0B", label: "Holiday", icon: <GlobalOutlined /> },
};

const PRIORITY_CFG = {
  low:    { color: "#9CA3AF" },
  normal: { color: "#3B82F6" },
  high:   { color: "#F59E0B" },
  urgent: { color: "#EF4444" },
};

const SCOPE_ICONS = {
  company:    <GlobalOutlined />,
  department: <BankOutlined />,
  team:       <TeamOutlined />,
};

const api = (token) =>
  axios.create({
    baseURL: `${SERVER_URL}/api/announcements`,
    headers: { Authorization: `Bearer ${token}` },
  });

const Announcements = () => {
  const loginUser = useSelector((s) => s?.loginUserInfo?.loginUserDetails);
  const token = localStorage.getItem("token");
  const isSuperAdmin = loginUser?.role === "Super Admin";
  const isAdmin = loginUser?.role === "Admin" || isSuperAdmin;

  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [form] = Form.useForm();

  const fetchData = useCallback(async (p = 1) => {
    try {
      const { data } = await api(token).get(`/?page=${p}&limit=50`);
      setAnnouncements(data.announcements || []);
      setTotal(data.total || 0);
      setUnreadCount(data.unreadCount || 0);
      setPage(p);
    } catch (err) {
      message.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);
      await api(token).post("/", values);
      message.success("Announcement published!");
      setShowCreate(false);
      form.resetFields();
      fetchData();
    } catch (err) {
      if (err.response?.data?.error) message.error(err.response.data.error);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api(token).delete(`/${id}`);
      message.success("Announcement deleted");
      fetchData(page);
    } catch {
      message.error("Failed to delete");
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await api(token).patch(`/${id}/read`);
      setAnnouncements((prev) =>
        prev.map((a) => (a._id === id ? { ...a, isRead: true } : a))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* silent */ }
  };

  // Filter by tab
  const filtered = activeTab === "all"
    ? announcements
    : activeTab === "unread"
      ? announcements.filter((a) => !a.isRead)
      : activeTab === "pinned"
        ? announcements.filter((a) => a.isPinned)
        : announcements.filter((a) => a.category === activeTab);

  // Department list for scope=department targeting
  const allUsers = useSelector((s) => s?.loginUserInfo?.allUsers) || [];
  const departments = [...new Set(allUsers.map((u) => u.department).filter(Boolean))].sort();

  if (loading) return <Spin size="large" style={{ display: "block", margin: "100px auto" }} />;

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              Announcements
              {unreadCount > 0 && (
                <Badge
                  count={unreadCount}
                  style={{ marginLeft: 12, backgroundColor: "#6366F1" }}
                />
              )}
            </Title>
          </Col>
          {isAdmin && (
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  form.resetFields();
                  form.setFieldsValue({
                    category: "general",
                    priority: "normal",
                    scope: isSuperAdmin ? "company" : "team",
                    isPinned: false,
                  });
                  setShowCreate(true);
                }}
              >
                New Announcement
              </Button>
            </Col>
          )}
        </Row>

        {/* Filter Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ marginBottom: 16 }}
          items={[
            { key: "all", label: `All (${announcements.length})` },
            { key: "unread", label: <span>Unread <Badge count={unreadCount} size="small" style={{ marginLeft: 4 }} /></span> },
            { key: "pinned", label: "Pinned" },
            ...Object.entries(CATEGORY_CFG).map(([k, v]) => ({
              key: k,
              label: <span>{v.icon} {v.label}</span>,
            })),
          ]}
        />

        {/* Announcements List */}
        {filtered.length === 0 ? (
          <Empty description="No announcements" style={{ padding: 60 }} />
        ) : (
          <Row gutter={[16, 16]}>
            {filtered.map((a) => {
              const cat = CATEGORY_CFG[a.category] || CATEGORY_CFG.general;
              const pri = PRIORITY_CFG[a.priority] || PRIORITY_CFG.normal;
              const isAuthor = a.createdBy === loginUser?.id;

              return (
                <Col xs={24} md={12} lg={8} key={a._id}>
                  <Card
                    hoverable
                    onClick={() => {
                      if (!a.isRead) handleMarkRead(a._id);
                    }}
                    style={{
                      borderLeft: `4px solid ${cat.color}`,
                      background: a.isRead ? "#fff" : "#f8f7ff",
                      height: "100%",
                    }}
                    bodyStyle={{ padding: 16 }}
                    actions={[
                      ...(isAuthor || isSuperAdmin
                        ? [
                            <Popconfirm
                              key="del"
                              title="Delete this announcement?"
                              onConfirm={(e) => { e.stopPropagation(); handleDelete(a._id); }}
                              onCancel={(e) => e.stopPropagation()}
                            >
                              <Tooltip title="Delete">
                                <DeleteOutlined
                                  style={{ color: "#EF4444" }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </Tooltip>
                            </Popconfirm>,
                          ]
                        : []),
                    ]}
                  >
                    {/* Pinned */}
                    {a.isPinned && (
                      <div style={{ marginBottom: 8 }}>
                        <Tag icon={<PushpinOutlined />} color="warning">
                          Pinned
                        </Tag>
                      </div>
                    )}

                    {/* Title + Unread dot */}
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                      {!a.isRead && (
                        <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: "#6366F1", marginTop: 6, flexShrink: 0 }} />
                      )}
                      <Text strong style={{ fontSize: 15, color: "#0f172a", flex: 1 }}>
                        {a.title}
                      </Text>
                    </div>

                    {/* Message */}
                    <Text style={{ fontSize: 13, color: "#475569", display: "block", marginBottom: 12, lineHeight: "20px" }}>
                      {a.message?.length > 200 ? a.message.slice(0, 200) + "..." : a.message}
                    </Text>

                    {/* Tags row */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                      <Tag color={cat.color}>{cat.icon} {cat.label}</Tag>
                      {a.priority !== "normal" && (
                        <Tag color={pri.color}>
                          {a.priority.charAt(0).toUpperCase() + a.priority.slice(1)}
                        </Tag>
                      )}
                      <Tag>
                        {SCOPE_ICONS[a.scope]} {a.scope === "company" ? "Company-wide" : a.scope === "department" ? "Department" : "Team"}
                      </Tag>
                    </div>

                    {/* Footer */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {a.createdByName || "Admin"} · {dayjs(a.createdAt).fromNow()}
                      </Text>
                      {a.readCount !== undefined && (
                        <Tooltip title={`${a.readCount} read`}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            <EyeOutlined /> {a.readCount}
                          </Text>
                        </Tooltip>
                      )}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Create Modal */}
        <Modal
          open={showCreate}
          title="New Announcement"
          onCancel={() => setShowCreate(false)}
          onOk={handleCreate}
          confirmLoading={creating}
          okText="Publish"
          width={560}
        >
          <Form form={form} layout="vertical" initialValues={{ category: "general", priority: "normal", scope: isSuperAdmin ? "company" : "team", isPinned: false }}>
            <Form.Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
              <Input placeholder="Announcement title" maxLength={200} showCount />
            </Form.Item>

            <Form.Item name="message" label="Message" rules={[{ required: true, message: "Message is required" }]}>
              <TextArea rows={4} placeholder="Write your announcement..." maxLength={2000} showCount />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="category" label="Category">
                  <Select
                    options={Object.entries(CATEGORY_CFG).map(([k, v]) => ({
                      value: k,
                      label: <span>{v.icon} {v.label}</span>,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="priority" label="Priority">
                  <Select
                    options={[
                      { value: "low", label: "Low" },
                      { value: "normal", label: "Normal" },
                      { value: "high", label: "High" },
                      { value: "urgent", label: "Urgent" },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            {isSuperAdmin && (
              <Form.Item name="scope" label="Audience">
                <Select
                  options={[
                    { value: "company", label: <span><GlobalOutlined /> Company-wide</span> },
                    { value: "department", label: <span><BankOutlined /> Specific Departments</span> },
                    { value: "team", label: <span><TeamOutlined /> My Team</span> },
                  ]}
                />
              </Form.Item>
            )}

            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.scope !== cur.scope}>
              {({ getFieldValue }) =>
                getFieldValue("scope") === "department" ? (
                  <Form.Item name="departments" label="Target Departments" rules={[{ required: true, message: "Select at least one department" }]}>
                    <Select mode="multiple" placeholder="Select departments" options={departments.map((d) => ({ value: d, label: d }))} />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="isPinned" label="Pin to top" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default Announcements;
